import watermarkLogo from '../assets/images/logo.webp';

const SNAPSHOTS_KEY = 'vto:snapshots';
const SELFIES_KEY = 'vto:selfies';

let _watermarkImg = null;
function getWatermarkImg() {
  if (typeof Image === 'undefined') return null;
  if (_watermarkImg) return _watermarkImg;
  const img = new Image();
  img.src = watermarkLogo;
  _watermarkImg = img;
  return img;
}

function drawMojeNaocaleWatermark(ctx, w, h, opts = {}) {
  if (!ctx || !w || !h) return;
  const opacity = typeof opts.opacity === 'number' ? opts.opacity : 0.6;
  const padding = typeof opts.padding === 'number' ? opts.padding : Math.round(Math.min(w, h) * 0.03);
  const targetH = typeof opts.height === 'number' ? opts.height : Math.round(h * 0.06); // ~6% of height

  const img = getWatermarkImg();
  ctx.save();
  ctx.globalAlpha = opacity;

  if (img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
    const aspect = img.naturalWidth / img.naturalHeight;
    const drawH = Math.max(18, Math.min(targetH, 64));
    const drawW = Math.round(drawH * aspect);
    const x = w - padding - drawW;
    const y = h - padding - drawH;
    ctx.drawImage(img, x, y, drawW, drawH);
  } else {
    // Fallback: text watermark until the logo is loaded
    const text = 'visionlive';
    ctx.font = `600 ${Math.max(12, Math.round(h * 0.022))}px "Google Sans", Inter, system-ui, -apple-system, Segoe UI, sans-serif`;
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    const metrics = ctx.measureText(text);
    const x = w - padding - metrics.width;
    const y = h - padding;
    // subtle shadow for contrast
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 6;
    ctx.fillText(text, x, y);
  }

  ctx.restore();
}

export function loadSnapshots() {
  try {
    const raw = localStorage.getItem(SNAPSHOTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveSnapshots(list) {
  try {
    localStorage.setItem(SNAPSHOTS_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

export function addSnapshot({ glassesId, dataUrl, max = 6 }) {
  const next = [
    {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      ts: Date.now(),
      glassesId,
      dataUrl,
    },
    ...loadSnapshots(),
  ].slice(0, max);
  saveSnapshots(next);
  return next;
}

export function loadSelfies() {
  try {
    const raw = localStorage.getItem(SELFIES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveSelfies(list) {
  try {
    localStorage.setItem(SELFIES_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

export function addSelfie({ baseFrameDataUrl, pose, stageW, stageH, mirrored = true, max = 12, compositeDataUrl = null, glassesId = null }) {
  const next = [
    {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      ts: Date.now(),
      baseFrameDataUrl,
      pose,
      stage: { w: stageW, h: stageH },
      mirrored,
      v: 1,
      compositeDataUrl: compositeDataUrl || undefined,
      glassesId: glassesId || undefined,
    },
    ...loadSelfies(),
  ].slice(0, max);
  saveSelfies(next);
  return next;
}

export function removeSelfie(selfieId) {
  const next = loadSelfies().filter((s) => s && s.id !== selfieId);
  saveSelfies(next);
  return next;
}

export function clearSelfies() {
  saveSelfies([]);
  return [];
}

export function getSelfieById(selfies, id) {
  return (selfies || []).find((s) => s && s.id === id) || null;
}

/**
 * Capture only the webcam video, matching the stage's object-fit: cover.
 * Optionally mirrors to match the live view.
 */
export function captureVideoCoverFrame({
  stageEl,
  videoEl,
  mirrored = true,
  watermark = true,
  mimeType = 'image/jpeg',
  quality = 0.9,
}) {
  if (!stageEl || !videoEl) throw new Error('stageEl/videoEl missing');

  const w = stageEl.clientWidth || 800;
  const h = stageEl.clientHeight || 600;

  const out = document.createElement('canvas');
  out.width = Math.round(w);
  out.height = Math.round(h);
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('2d context missing');

  const vw = videoEl.videoWidth || w;
  const vh = videoEl.videoHeight || h;

  const scale = Math.max(w / vw, h / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  const dx = (w - dw) / 2;
  const dy = (h - dh) / 2;

  ctx.save();
  if (mirrored) {
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(videoEl, dx, dy, dw, dh);
  } else {
    ctx.drawImage(videoEl, dx, dy, dw, dh);
  }
  ctx.restore();

  if (watermark) {
    drawMojeNaocaleWatermark(ctx, w, h, { opacity: 0.6 });
  }

  return out.toDataURL(mimeType, quality);
}

/**
 * Captures a composite image of:
 * - webcam video (object-fit: cover + optionally mirrored)
 * - scanCanvas (2D overlay)
 * - threeCanvas (WebGL overlay)
 *
 * Returns a dataURL (jpeg by default for size).
 */
export function captureStageComposite({
  stageEl,
  videoEl,
  scanCanvas,
  threeCanvas,
  mirrored = true,
  watermark = true,
  mimeType = 'image/jpeg',
  quality = 0.88,
}) {
  if (!stageEl || !videoEl) throw new Error('stageEl/videoEl missing');

  const w = stageEl.clientWidth || 800;
  const h = stageEl.clientHeight || 600;

  const out = document.createElement('canvas');
  out.width = Math.round(w);
  out.height = Math.round(h);
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('2d context missing');

  // Draw webcam with `objectFit: cover` into stage rect.
  const vw = videoEl.videoWidth || w;
  const vh = videoEl.videoHeight || h;

  const scale = Math.max(w / vw, h / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  const dx = (w - dw) / 2;
  const dy = (h - dh) / 2;

  ctx.save();
  if (mirrored) {
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(videoEl, dx, dy, dw, dh);
  } else {
    ctx.drawImage(videoEl, dx, dy, dw, dh);
  }
  ctx.restore();

  // Overlays are already in stage coordinates.
  if (scanCanvas) {
    ctx.drawImage(scanCanvas, 0, 0, w, h);
  }
  if (threeCanvas) {
    ctx.drawImage(threeCanvas, 0, 0, w, h);
  }

  if (watermark) {
    drawMojeNaocaleWatermark(ctx, w, h, { opacity: 0.6 });
  }

  return out.toDataURL(mimeType, quality);
}

export function findLatestSnapshotForGlasses(snapshots, glassesId) {
  if (!Array.isArray(snapshots)) return null;
  const found = snapshots.find((s) => s && s.glassesId === glassesId);
  return found || null;
}


